import { useMemo, useCallback } from 'react';
import { useTimeManager } from './useTimeManager';
import { useBusinesses } from '@/hooks/business/useBusinesses';

interface UseBusinessTimeProps {
  updateInterval?: number;
  format?: 'full' | 'short';
}

interface UseBusinessTimeReturn {
  currentTime: Date;
  formattedTime: string;
  businessTimezone: string;
  countryCode: string | null;
  formatRelativeTime: (timestamp: string) => string;
  formatCurrentTime: (format?: 'full' | 'short') => string;
  // Additional timezone-specific formatting helpers
  formatTimeInBusinessTimezone: (timeFormat?: Intl.DateTimeFormatOptions) => string;
  formatDateInBusinessTimezone: (dateFormat?: Intl.DateTimeFormatOptions) => string;
}

/**
 * Hook that provides current time in the business location timezone
 * Automatically fetches business data and applies appropriate timezone
 */
export const useBusinessTime = ({
  updateInterval = 1000,
  format = 'short'
}: UseBusinessTimeProps = {}): UseBusinessTimeReturn => {
  const { selectedBusinessId, businesses } = useBusinesses();

  // Get selected business directly from businesses array
  const selectedBusiness = useMemo(() => {
    return businesses?.find(business => business.id === selectedBusinessId) || null;
  }, [businesses, selectedBusinessId]);

  // Extract country code from selected business (memoized separately for efficiency)
  const countryCode = useMemo(() => {
    return selectedBusiness?.country_code || null;
  }, [selectedBusiness?.country_code]);

  // Determine business timezone - use database timezone or browser timezone (no country fallback)
  // Memoized to prevent recalculation on every time update
  const businessTimezone = useMemo(() => {
    // First priority: timezone field from business data
    if (selectedBusiness?.timezone) {
      return selectedBusiness.timezone;
    }
    // Fallback to browser timezone (neutral, no country assumptions)
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [selectedBusiness?.timezone]);

  // Use enhanced time manager with business timezone
  const timeData = useTimeManager({
    mode: 'current',
    updateInterval,
    format,
    businessTimezone
  });

  // Cached formatters to avoid recreation on every call - performance optimization
  const defaultTimeFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: businessTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.warn('Failed to create timezone-aware time formatter, using fallback:', error);
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  }, [businessTimezone]);

  const defaultDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: businessTimezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.warn('Failed to create timezone-aware date formatter, using fallback:', error);
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  }, [businessTimezone]);

  // Additional formatting helpers that use business timezone
  const formatTimeInBusinessTimezone = useCallback((timeFormat?: Intl.DateTimeFormatOptions) => {
    if (!timeFormat) {
      // Use cached formatter for default format
      return defaultTimeFormatter.format(timeData.currentTime);
    }

    // For custom formats, create formatter as needed (less common path)
    try {
      const customFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: businessTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...timeFormat
      });
      return customFormatter.format(timeData.currentTime);
    } catch (error) {
      console.warn('Failed to format time in business timezone:', error);
      return defaultTimeFormatter.format(timeData.currentTime);
    }
  }, [timeData.currentTime, businessTimezone, defaultTimeFormatter]);

  const formatDateInBusinessTimezone = useCallback((dateFormat?: Intl.DateTimeFormatOptions) => {
    if (!dateFormat) {
      // Use cached formatter for default format
      return defaultDateFormatter.format(timeData.currentTime);
    }

    // For custom formats, create formatter as needed (less common path)
    try {
      const customFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: businessTimezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...dateFormat
      });
      return customFormatter.format(timeData.currentTime);
    } catch (error) {
      console.warn('Failed to format date in business timezone:', error);
      return defaultDateFormatter.format(timeData.currentTime);
    }
  }, [timeData.currentTime, businessTimezone, defaultDateFormatter]);

  return {
    ...timeData,
    businessTimezone,
    countryCode,
    formatTimeInBusinessTimezone,
    formatDateInBusinessTimezone
  };
};

/**
 * Hook specifically for calendar components that need business time updates
 * Updated to reduce re-render frequency from every second to every minute
 */
export const useBusinessTimeForCalendar = () => {
  return useBusinessTime({
    updateInterval: 60000, // Update every minute to reduce re-renders while maintaining red line functionality
    format: 'short'
  });
};

/**
 * Hook for presence cards and dashboard components
 */
export const useBusinessTimeForDashboard = () => {
  return useBusinessTime({
    updateInterval: 60000, // Update every minute for dashboard
    format: 'short'
  });
};